/* Unified Brand Profile manager — ONE profile powers all four studios
   (Content Intelligence, Video, Post, Flyer). Manages social_brand_profiles
   as the master record; logo / headshots / palette upload through the linked
   flyer brand (profile.flyer_brand_id) which the backend keeps in sync.

   Supports hierarchical profiles: a parent "group" profile (e.g. "Purposefully
   Covered Group") can hold sub-profiles (Final Expense, Life, Medicare...). */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Save, Trash2, Upload, Image as ImageIcon, Palette, Loader2, Sparkles,
  Star, FolderTree, ChevronRight, ChevronDown, Layers, CopyCheck, Rocket,
  CalendarClock, CalendarCheck, Clock,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API_URL, withCredentials: true });
const errMsg = (e, f) => { const d = e?.response?.data?.detail; return !d ? f : (typeof d === 'string' ? d : d.message || f); };
const readFile = (file) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });

const DEFAULT_COLORS = { primary: '#1E5BA8', primary_dark: '#0A3D7A', secondary: '#E27A22', accent: '#F2954A' };
const EMPTY = {
  name: '', niche: '', ideal_viewer: '', unique_angle: '', core_message: '',
  style: 'Educational, conversational', tone: 'Confident, warm, direct',
  parent_id: null, content_pillars: [],
  agent_name: '', title: '', phone: '', email: '', website: '',
  license_no: '', license_state: '', states: [], coverage_options: [],
  tagline: '', disclaimer: '', colors: { ...DEFAULT_COLORS },
};

function TextField({ label, value, onChange, testid, col2, textarea, placeholder }) {
  return (
    <div className={col2 ? 'sm:col-span-2' : ''}>
      <label className="text-[11px] font-bold uppercase tracking-wide text-[#64748B]">{label}</label>
      {textarea
        ? <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={testid} rows={2} placeholder={placeholder || ''} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm focus:outline-none focus:border-[#1D4ED8]" />
        : <input value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder || ''} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm focus:outline-none focus:border-[#1D4ED8]" />}
    </div>
  );
}

export default function BrandProfileManager({ renderCardExtras, editorExtras, onEditingChange, onChanged }) {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState('');
  const [removeBg, setRemoveBg] = useState(true);
  const [pillarInput, setPillarInput] = useState('');
  const [expanded, setExpanded] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkPlatform, setBulkPlatform] = useState('instagram');
  const [schedRows, setSchedRows] = useState(null);
  const [schedBusy, setSchedBusy] = useState(false);
  const [schedDone, setSchedDone] = useState(false);
  const logoInput = useRef(null);
  const headInput = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/api/admin/social-studio/brand-profiles'); setProfiles(r.data.profiles || []); }
    catch (e) { toast.error(errMsg(e, 'Could not load profiles')); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { onEditingChange?.(editingId); }, [editingId, onEditingChange]);

  const f = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const editing = editingId ? profiles.find((p) => p.profile_id === editingId) : null;
  const parents = profiles.filter((p) => !p.parent_id);

  const startNew = (parentId = null) => {
    setEditingId(null);
    setForm({ ...EMPTY, colors: { ...DEFAULT_COLORS }, parent_id: parentId });
  };
  const startEdit = (p) => {
    setEditingId(p.profile_id);
    setForm({
      ...EMPTY, ...p,
      colors: { ...DEFAULT_COLORS, ...(p.colors || {}) },
      content_pillars: p.content_pillars || [], states: p.states || [], coverage_options: p.coverage_options || [],
    });
  };

  const save = async () => {
    if (!form.name?.trim() || !form.niche?.trim() || !form.ideal_viewer?.trim()) {
      toast.error('Name, niche, and ideal viewer are required'); return;
    }
    setSaving(true);
    const payload = { ...form, parent_id: form.parent_id || null };
    try {
      let pid = editingId;
      if (editingId) { await api.patch(`/api/admin/social-studio/brand-profiles/${editingId}`, payload); toast.success('Profile saved'); }
      else { const r = await api.post('/api/admin/social-studio/brand-profiles', payload); pid = r.data.profile_id; setEditingId(pid); toast.success('Profile created — now add a logo & headshots'); }
      await load(); onChanged?.();
      const fresh = (await api.get('/api/admin/social-studio/brand-profiles')).data.profiles?.find((x) => x.profile_id === pid);
      if (fresh) startEdit(fresh);
    } catch (e) { toast.error(errMsg(e, 'Save failed')); } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this brand profile? Sub-profiles will be promoted to top-level.')) return;
    try { await api.delete(`/api/admin/social-studio/brand-profiles/${id}`); if (editingId === id) startNew(); await load(); onChanged?.(); toast.success('Deleted'); }
    catch (e) { toast.error(errMsg(e, 'Delete failed')); }
  };

  const applyBrandingToSubs = async () => {
    if (!editingId) return;
    const n = profiles.filter((p) => p.parent_id === editingId).length;
    if (!n) { toast.error('This profile has no sub-profiles yet'); return; }
    if (!window.confirm(`Apply this group's branding (logo, headshots, brand colors, contact info, tagline & disclaimer) to all ${n} sub-profile(s)? This overwrites their existing branding.`)) return;
    setSaving(true);
    try {
      // persist any unsaved edits on the parent first, then cascade
      await api.patch(`/api/admin/social-studio/brand-profiles/${editingId}`, { ...form, parent_id: form.parent_id || null });
      const r = await api.post(`/api/admin/social-studio/brand-profiles/${editingId}/apply-branding-to-subs`);
      toast.success(`Branding applied to ${r.data.updated} sub-profile(s)`);
      await load(); onChanged?.();
    } catch (e) { toast.error(errMsg(e, 'Could not apply branding')); } finally { setSaving(false); }
  };

  const bulkGenerateWeek = async () => {
    if (!editingId) return;
    const n = profiles.filter((p) => p.parent_id === editingId).length;
    if (!n) { toast.error('This profile has no sub-profiles yet'); return; }
    if (!window.confirm(`Generate this week's Hook -> Value -> CTA post for all ${n} sub-profile(s) on ${bulkPlatform}? They'll be saved as drafts in Post Studio.`)) return;
    setBulkBusy(true); setBulkResult(null);
    try {
      const r = await api.post(`/api/admin/social-studio/brand-profiles/${editingId}/bulk-generate-week`, { platform: bulkPlatform });
      const jobId = r.data.job_id;
      toast.success(`Writing ${r.data.total} posts in the background…`);
      const poll = async () => {
        try {
          const j = await api.get(`/api/admin/social-studio/brand-profiles/bulk-week-jobs/${jobId}`);
          setBulkResult(j.data);
          if (j.data.status === 'done') {
            setBulkBusy(false);
            const ok = (j.data.results || []).length; const bad = (j.data.errors || []).length;
            if (ok) toast.success(`Done — ${ok} post(s) saved to Post Studio drafts`);
            if (bad) toast.error(`${bad} could not be generated`);
            return;
          }
          setTimeout(poll, 3000);
        } catch (e) { setBulkBusy(false); toast.error(errMsg(e, 'Lost track of the job')); }
      };
      setTimeout(poll, 2500);
    } catch (e) { setBulkBusy(false); toast.error(errMsg(e, 'Bulk generate failed')); }
  };

  // When bulk generation finishes, seed an editable weekly schedule (one post per day,
  // spread across the upcoming 7 days). The user can change each post's date AND time.
  const toDateInput = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  useEffect(() => {
    if (bulkResult?.status === 'done' && (bulkResult.results || []).length) {
      setSchedDone(false);
      setSchedRows((prev) => {
        if (prev) return prev; // keep user edits across re-polls
        const base = new Date(); base.setDate(base.getDate() + 1); base.setHours(9, 0, 0, 0);
        return bulkResult.results.map((r, i) => {
          const d = new Date(base); d.setDate(base.getDate() + i);
          return { post_id: r.post_id, profile_name: r.profile_name, topic: r.topic, date: toDateInput(d), time: '09:00' };
        });
      });
    } else if (!bulkResult) {
      setSchedRows(null); setSchedDone(false);
    }
  }, [bulkResult]);

  const setSchedField = (post_id, key, value) =>
    setSchedRows((rows) => rows.map((r) => (r.post_id === post_id ? { ...r, [key]: value } : r)));

  const scheduleAllDrafts = async () => {
    if (!schedRows?.length) return;
    const invalid = schedRows.find((r) => !r.date || !r.time);
    if (invalid) { toast.error('Pick a date and time for every post'); return; }
    setSchedBusy(true);
    let ok = 0, fail = 0;
    for (const row of schedRows) {
      try {
        const iso = new Date(`${row.date}T${row.time}`).toISOString();
        await api.post(`/api/admin/social-studio/posts/${row.post_id}/schedule`, { publish_at: iso });
        ok += 1;
      } catch { fail += 1; }
    }
    setSchedBusy(false);
    if (ok) { toast.success(`Scheduled ${ok} post(s) to the calendar`); setSchedDone(true); }
    if (fail) toast.error(`${fail} post(s) could not be scheduled`);
    onChanged?.();
  };

  const uploadLogo = async (file) => {
    if (!editing?.flyer_brand_id) { toast.error('Save the profile first, then add a logo'); return; }
    setUploading('logo');
    try {
      const dataUrl = await readFile(file);
      const r = await api.post(`/api/flyer-studio/brands/${editing.flyer_brand_id}/logo`, { data_url: dataUrl });
      if (r.data.colors) f('colors', { ...DEFAULT_COLORS, ...r.data.colors });
      f('logo_url', r.data.logo_url);
      toast.success('Logo uploaded — palette extracted'); await load();
    } catch (e) { toast.error(errMsg(e, 'Upload failed')); } finally { setUploading(''); }
  };
  const uploadHeadshots = async (files) => {
    if (!editing?.flyer_brand_id) { toast.error('Save the profile first, then add headshots'); return; }
    setUploading('headshot');
    try {
      let last = null;
      for (const file of Array.from(files)) {
        const dataUrl = await readFile(file);
        const r = await api.post(`/api/flyer-studio/brands/${editing.flyer_brand_id}/headshot`, { data_url: dataUrl, remove_background: removeBg });
        last = r.data;
      }
      if (last) { f('headshots', last.headshots || []); f('headshot_url', last.headshot_url); }
      toast.success('Headshots added'); await load();
    } catch (e) { toast.error(errMsg(e, 'Upload failed')); } finally { setUploading(''); }
  };
  const setDefaultHead = async (id) => {
    try { const r = await api.post(`/api/flyer-studio/brands/${editing.flyer_brand_id}/headshots/${id}/default`); f('headshot_url', r.data.headshot_url); await load(); }
    catch { toast.error('Could not set default'); }
  };
  const delHead = async (id) => {
    try { const r = await api.delete(`/api/flyer-studio/brands/${editing.flyer_brand_id}/headshots/${id}`); f('headshots', r.data.headshots || []); f('headshot_url', r.data.headshot_url); await load(); }
    catch { toast.error('Could not delete'); }
  };

  const addPillar = () => {
    if (!pillarInput.trim()) return;
    f('content_pillars', Array.from(new Set([...(form.content_pillars || []), pillarInput.trim()])));
    setPillarInput('');
  };

  const headshots = form.headshots || [];

  if (loading) return <div className="py-16 text-center"><Loader2 className="animate-spin mx-auto text-[#94A3B8]" /></div>;

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="brand-profile-manager">
      {/* ============ LIST (grouped by parent) ============ */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C] flex items-center gap-2"><Layers size={15} className="text-[#1E5BA8]" /> Your Brand Profiles</h2>
          <button onClick={() => startNew()} data-testid="bpm-new-group" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1E5BA8] text-white text-xs font-bold hover:bg-[#1A4F94]"><Plus size={13} /> New profile</button>
        </div>
        <p className="text-[11px] text-[#94A3B8]">One profile powers all four studios. Create a parent group (e.g. <b>Purposefully Covered Group</b>) and nest sub-profiles (Final Expense, Life, Medicare…) under it.</p>

        {parents.length === 0 && <div className="text-sm text-[#64748B] py-8 text-center bg-white border border-[#E2E8F0] rounded-2xl">No profiles yet. Create your first Brand Profile.</div>}

        {parents.map((parent) => {
          const subs = profiles.filter((p) => p.parent_id === parent.profile_id);
          const isOpen = !!expanded[parent.profile_id];
          return (
            <div key={parent.profile_id} className="space-y-2">
              <ProfileCard p={parent} isParent={subs.length > 0} subCount={subs.length}
                expanded={isOpen} onToggleExpand={subs.length ? () => setExpanded((e) => ({ ...e, [parent.profile_id]: !e[parent.profile_id] })) : null}
                editingId={editingId} onEdit={startEdit} onDel={del} onAddSub={() => startNew(parent.profile_id)} renderCardExtras={renderCardExtras} />
              {subs.length > 0 && isOpen && (
                <div className="ml-4 pl-3 border-l-2 border-[#E2E8F0] space-y-2" data-testid={`bpm-subs-${parent.profile_id}`}>
                  {subs.map((s) => (
                    <ProfileCard key={s.profile_id} p={s} sub editingId={editingId} onEdit={startEdit} onDel={del} renderCardExtras={renderCardExtras} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ============ EDITOR ============ */}
      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-4" data-testid="bpm-editor">
        <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">{editingId ? `Edit: ${form.name}` : 'New Brand Profile'}</h2>

        {/* Hierarchy */}
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wide text-[#64748B] flex items-center gap-1"><FolderTree size={12} /> Parent group (optional)</label>
          <select value={form.parent_id || ''} onChange={(e) => f('parent_id', e.target.value || null)} data-testid="bpm-parent-select" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm">
            <option value="">— None (this is a top-level profile/group) —</option>
            {parents.filter((p) => p.profile_id !== editingId).map((p) => <option key={p.profile_id} value={p.profile_id}>{p.name}</option>)}
          </select>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="Profile name *" col2 value={form.name} onChange={(v) => f('name', v)} testid="bpm-name" placeholder="e.g. Final Expense Insurance" />
          <TextField label="Niche / industry *" col2 value={form.niche} onChange={(v) => f('niche', v)} testid="bpm-niche" />
          <TextField label="Ideal viewer *" col2 textarea value={form.ideal_viewer} onChange={(v) => f('ideal_viewer', v)} testid="bpm-viewer" />
          <TextField label="Unique angle" col2 textarea value={form.unique_angle} onChange={(v) => f('unique_angle', v)} testid="bpm-angle" />
          <TextField label="Core message" col2 textarea value={form.core_message} onChange={(v) => f('core_message', v)} testid="bpm-core" />
          <TextField label="Style" value={form.style} onChange={(v) => f('style', v)} testid="bpm-style" />
          <TextField label="Tone" value={form.tone} onChange={(v) => f('tone', v)} testid="bpm-tone" />
        </div>

        {/* Content pillars */}
        <div data-testid="bpm-pillars">
          <label className="text-[11px] font-bold uppercase tracking-wide text-[#64748B]">Content pillars</label>
          <div className="flex flex-wrap gap-1.5 my-1.5">
            {(form.content_pillars || []).map((p, i) => (
              <span key={i} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-[#EFF6FF] text-[#1D4ED8] rounded-full">{p}
                <button type="button" aria-label={`Remove ${p}`} onClick={() => f('content_pillars', (form.content_pillars || []).filter((_, idx) => idx !== i))} className="hover:text-red-600">×</button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input value={pillarInput} onChange={(e) => setPillarInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addPillar(); } }} placeholder="Add a pillar, press Enter" data-testid="bpm-pillar-input" className="flex-1 px-3 py-1.5 border border-[#E2E8F0] rounded-lg text-xs" />
          </div>
        </div>

        {/* Visual + contact (powers Flyer Studio) */}
        <div className="border-t border-[#E2E8F0] pt-4">
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#0A0F1C] mb-3 flex items-center gap-1.5"><ImageIcon size={13} className="text-[#1E5BA8]" /> Branding & contact <span className="text-[10px] font-medium text-[#94A3B8] normal-case">— shown on your flyers & posts</span></h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <TextField label="Agent name" value={form.agent_name} onChange={(v) => f('agent_name', v)} testid="bpm-agent" />
            <TextField label="Title" value={form.title} onChange={(v) => f('title', v)} testid="bpm-titlefield" />
            <TextField label="Phone" value={form.phone} onChange={(v) => f('phone', v)} testid="bpm-phone" />
            <TextField label="Email" value={form.email} onChange={(v) => f('email', v)} testid="bpm-email" />
            <TextField label="Website" value={form.website} onChange={(v) => f('website', v)} testid="bpm-website" />
            <TextField label="License #" value={form.license_no} onChange={(v) => f('license_no', v)} testid="bpm-license" />
            <TextField label="States served (comma)" col2 value={(form.states || []).join(', ')} onChange={(v) => f('states', v.split(',').map((x) => x.trim()).filter(Boolean))} testid="bpm-states" />
            <TextField label="Coverage / offerings (comma)" col2 textarea value={(form.coverage_options || []).join(', ')} onChange={(v) => f('coverage_options', v.split(',').map((x) => x.trim()).filter(Boolean))} testid="bpm-coverage" />
            <TextField label="Tagline" col2 value={form.tagline} onChange={(v) => f('tagline', v)} testid="bpm-tagline" />
            <TextField label="Compliance disclaimer" col2 textarea value={form.disclaimer} onChange={(v) => f('disclaimer', v)} testid="bpm-disclaimer" />
          </div>
        </div>

        {/* Brand colors */}
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wide text-[#64748B] flex items-center gap-1.5"><Palette size={12} /> Brand colors</label>
          <div className="flex gap-3 flex-wrap mt-1.5">
            {['primary', 'primary_dark', 'secondary', 'accent'].map((k) => (
              <div key={k} className="text-center">
                <input type="color" value={form.colors?.[k] || '#E2E8F0'} onChange={(e) => f('colors', { ...form.colors, [k]: e.target.value })} data-testid={`bpm-color-${k}`} className="w-12 h-12 rounded-lg border border-[#E2E8F0] cursor-pointer" />
                <div className="text-[9px] text-[#94A3B8] mt-1">{k.replace('_', ' ')}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Logo + headshots (require saved profile w/ linked brand) */}
        {editingId ? (
          <div className="border-t border-[#E2E8F0] pt-4 space-y-4">
            <div>
              <h3 className="font-bold text-[#0A0F1C] mb-2 text-sm">Logo</h3>
              <div className="flex items-center gap-4">
                <div className="w-24 h-24 rounded-xl border-2 border-dashed border-[#E2E8F0] flex items-center justify-center overflow-hidden bg-[#F8FAFC] flex-shrink-0">
                  {form.logo_url ? <img src={form.logo_url} alt="logo" className="max-h-full max-w-full object-contain" /> : <ImageIcon className="text-[#CBD5E1]" size={28} />}
                </div>
                <div>
                  <input ref={logoInput} type="file" accept="image/*" className="hidden" data-testid="bpm-logo-input" onChange={(e) => e.target.files[0] && uploadLogo(e.target.files[0])} />
                  <button onClick={() => logoInput.current?.click()} disabled={uploading === 'logo'} data-testid="bpm-logo-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#E2E8F0] text-xs font-bold text-[#475569] hover:border-[#1E5BA8]">{uploading === 'logo' ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload logo</button>
                  <p className="text-[11px] text-[#94A3B8] mt-2">Palette auto-extracts from your logo.</p>
                </div>
              </div>
            </div>
            <div data-testid="bpm-headshots">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-[#0A0F1C] text-sm">Headshot library</h3>
                <input ref={headInput} type="file" accept="image/*" multiple className="hidden" data-testid="bpm-head-input" onChange={(e) => e.target.files.length && uploadHeadshots(e.target.files)} />
                <button onClick={() => headInput.current?.click()} disabled={uploading === 'headshot'} data-testid="bpm-head-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1E5BA8] text-white text-xs font-bold hover:bg-[#1A4F94]">{uploading === 'headshot' ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Add photos</button>
              </div>
              {headshots.length === 0 ? <p className="text-xs text-[#94A3B8]">No headshots yet.</p> : (
                <div className="grid grid-cols-4 gap-3">
                  {headshots.map((h) => {
                    const isDefault = form.headshot_url === h.url;
                    return (
                      <div key={h.headshot_id} className="relative" data-testid={`bpm-headshot-${h.headshot_id}`}>
                        <div className={`aspect-square rounded-xl overflow-hidden border-2 ${isDefault ? 'border-[#1E5BA8]' : 'border-[#E2E8F0]'}`}><img src={h.url} alt={h.label} className="w-full h-full object-cover" /></div>
                        {isDefault && <span className="absolute top-1 left-1 bg-[#1E5BA8] text-white text-[9px] font-bold px-1.5 py-0.5 rounded inline-flex items-center gap-0.5"><Star size={9} /> Default</span>}
                        <div className="flex gap-1 mt-1">
                          {!isDefault && <button onClick={() => setDefaultHead(h.headshot_id)} className="flex-1 text-[10px] font-bold text-[#1E5BA8] hover:underline">Set default</button>}
                          <button onClick={() => delHead(h.headshot_id)} className="text-[#94A3B8] hover:text-[#DC2626]" aria-label="Delete"><Trash2 size={12} /></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <label className="flex items-center gap-2 mt-2 text-xs text-[#475569] font-semibold"><input type="checkbox" checked={removeBg} onChange={(e) => setRemoveBg(e.target.checked)} data-testid="bpm-removebg" /> Auto-remove background (AI) on upload</label>
            </div>
          </div>
        ) : (
          <p className="text-[11px] text-[#94A3B8] border-t border-[#E2E8F0] pt-3">Save the profile to unlock logo & headshot uploads.</p>
        )}

        <div className="flex items-center gap-2 border-t border-[#E2E8F0] pt-4 flex-wrap">
          <button onClick={save} disabled={saving} data-testid="bpm-save" className="inline-flex items-center gap-2 px-4 py-2 bg-[#1D4ED8] text-white text-sm font-bold rounded-lg disabled:opacity-60">{saving ? <Loader2 size={14} className="animate-spin" /> : (editingId ? <Save size={14} /> : <Plus size={14} />)} {editingId ? 'Save changes' : 'Create profile'}</button>
          {editingId && profiles.filter((p) => p.parent_id === editingId).length > 0 && (
            <button onClick={applyBrandingToSubs} disabled={saving} data-testid="bpm-cascade-branding" title="Copy this group's logo, headshots, colors & contact info to every sub-profile" className="inline-flex items-center gap-2 px-4 py-2 border-2 border-[#1E5BA8] text-[#1E5BA8] text-sm font-bold rounded-lg hover:bg-[#EFF6FF] disabled:opacity-60"><CopyCheck size={14} /> Apply branding to {profiles.filter((p) => p.parent_id === editingId).length} sub-profile(s)</button>
          )}
          {editingId && <button onClick={() => startNew()} className="text-xs text-[#64748B] underline" data-testid="bpm-cancel">Cancel / New</button>}
        </div>

        {editingId && profiles.filter((p) => p.parent_id === editingId).length > 0 && (
          <div className="border-t border-[#E2E8F0] pt-4 space-y-3" data-testid="bpm-bulk-week">
            <div className="flex items-center gap-2">
              <Rocket size={15} className="text-[#7C3AED]" />
              <h3 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Bulk-generate this week's content</h3>
            </div>
            <p className="text-[11px] text-[#64748B]">One click generates a fresh <b>Hook → Value → CTA</b> post for every sub-profile under this group. They're saved as drafts in Post Studio under each product line.</p>
            <div className="flex items-center gap-2 flex-wrap">
              <select value={bulkPlatform} onChange={(e) => setBulkPlatform(e.target.value)} data-testid="bpm-bulk-platform" className="px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm">
                {['instagram', 'facebook', 'tiktok', 'linkedin', 'youtube', 'x'].map((p) => <option key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</option>)}
              </select>
              <button onClick={bulkGenerateWeek} disabled={bulkBusy} data-testid="bpm-bulk-generate" className="inline-flex items-center gap-2 px-4 py-2 bg-[#7C3AED] text-white text-sm font-bold rounded-lg hover:bg-[#6D28D9] disabled:opacity-60">
                {bulkBusy ? <Loader2 size={14} className="animate-spin" /> : <Rocket size={14} />} Generate for all {profiles.filter((p) => p.parent_id === editingId).length} sub-profiles
              </button>
            </div>
            {bulkBusy && <p className="text-[11px] text-[#94A3B8]">Writing posts for each product line{bulkResult ? ` — ${bulkResult.done || 0}/${bulkResult.total || 0} done` : ''}… this can take up to a minute.</p>}
            {bulkResult && (
              <div className="space-y-1.5" data-testid="bpm-bulk-result">
                <p className="text-xs font-bold text-[#0A0F1C]">{(bulkResult.results || []).length} generated{(bulkResult.errors || []).length ? ` · ${bulkResult.errors.length} failed` : ''}{bulkResult.status !== 'done' ? ' (in progress…)' : ''}</p>
                {(bulkResult.results || []).map((r) => (
                  <div key={r.post_id} className="bg-[#F8FAFC] border border-[#F1F5F9] rounded-lg p-2 text-xs">
                    <span className="font-bold text-[#7C3AED]">{r.profile_name}</span>
                    <span className="text-[#64748B]"> — {r.topic}</span>
                    {r.hook && <div className="text-[#334155] mt-0.5"><b>Hook:</b> {r.hook}</div>}
                  </div>
                ))}
                {(bulkResult.errors || []).map((e, i) => (
                  <div key={i} className="bg-red-50 border border-red-100 rounded-lg p-2 text-xs text-red-700">
                    <b>{e.profile_name}:</b> {e.error}
                  </div>
                ))}
              </div>
            )}

            {/* Bulk schedule — push the generated drafts onto the content calendar, one per day,
                with full per-post control over the publish date AND time. */}
            {schedRows && schedRows.length > 0 && (
              <div className="border-t border-[#E2E8F0] pt-4 space-y-3" data-testid="bpm-bulk-schedule">
                <div className="flex items-center gap-2">
                  <CalendarClock size={15} className="text-[#1E5BA8]" />
                  <h3 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Schedule across the week</h3>
                </div>
                <p className="text-[11px] text-[#64748B]">Each draft is pre-spread one per day. Adjust the <b>date and time</b> for every post, then push them all to the content calendar.</p>
                <div className="space-y-2">
                  {schedRows.map((row) => (
                    <div key={row.post_id} className="flex items-center gap-2 flex-wrap bg-[#F8FAFC] border border-[#F1F5F9] rounded-lg p-2" data-testid={`bpm-sched-row-${row.post_id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-bold text-[#7C3AED] truncate">{row.profile_name}</div>
                        <div className="text-[11px] text-[#64748B] truncate">{row.topic}</div>
                      </div>
                      <input type="date" value={row.date} onChange={(e) => setSchedField(row.post_id, 'date', e.target.value)} data-testid={`bpm-sched-date-${row.post_id}`} className="px-2 py-1.5 rounded-lg border border-[#E2E8F0] text-xs" />
                      <input type="time" value={row.time} onChange={(e) => setSchedField(row.post_id, 'time', e.target.value)} data-testid={`bpm-sched-time-${row.post_id}`} className="px-2 py-1.5 rounded-lg border border-[#E2E8F0] text-xs" />
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={scheduleAllDrafts} disabled={schedBusy} data-testid="bpm-sched-all" className="inline-flex items-center gap-2 px-4 py-2 bg-[#1E5BA8] text-white text-sm font-bold rounded-lg hover:bg-[#1A4F94] disabled:opacity-60">
                    {schedBusy ? <Loader2 size={14} className="animate-spin" /> : <CalendarCheck size={14} />} Schedule all {schedRows.length} post(s)
                  </button>
                  {schedDone && <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-600" data-testid="bpm-sched-done"><Clock size={13} /> Added to the calendar</span>}
                </div>
              </div>
            )}
          </div>
        )}

        {editingId && editorExtras}
      </div>
    </div>
  );
}

function ProfileCard({ p, sub, isParent, subCount, expanded, onToggleExpand, editingId, onEdit, onDel, onAddSub, renderCardExtras }) {
  return (
    <div className={`bg-white border rounded-2xl p-4 ${editingId === p.profile_id ? 'border-[#1D4ED8] ring-1 ring-[#1D4ED8]' : 'border-[#E2E8F0]'}`} data-testid={`bpm-card-${p.profile_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex items-start gap-1.5">
          {onToggleExpand && (
            <button onClick={onToggleExpand} data-testid={`bpm-toggle-${p.profile_id}`} aria-label={expanded ? 'Collapse' : 'Expand'} className="mt-0.5 text-[#64748B] hover:text-[#1E5BA8] flex-shrink-0">
              {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
          )}
          <div className="min-w-0">
            <h3 className="font-bold text-[#0A0F1C] text-sm flex items-center gap-1.5 flex-wrap">
              {sub && <ChevronRight size={13} className="text-[#94A3B8]" />}
              {p.logo_url && <img src={p.logo_url} alt="" className="w-5 h-5 rounded object-contain" />}
              {p.name}
              {isParent && <span className="text-[9px] uppercase tracking-wider text-[#1E5BA8] bg-[#EFF6FF] px-1.5 py-0.5 rounded">group · {subCount}</span>}
              {p.is_seed && <span className="text-[9px] uppercase tracking-wider text-[#94A3B8]">seed</span>}
            </h3>
            <p className="text-[11px] text-[#64748B] mt-1 line-clamp-1"><b className="text-[#475569]">Niche:</b> {p.niche}</p>
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {onAddSub && <button onClick={onAddSub} data-testid={`bpm-addsub-${p.profile_id}`} className="text-[#1E5BA8] hover:underline text-[11px] font-semibold inline-flex items-center gap-0.5"><Plus size={11} /> Sub</button>}
          <button onClick={() => onEdit(p)} data-testid={`bpm-edit-${p.profile_id}`} className="text-[#1D4ED8] hover:underline text-[11px] font-semibold">Edit</button>
          {!p.is_seed && <button onClick={() => onDel(p.profile_id)} data-testid={`bpm-del-${p.profile_id}`} className="text-red-600 hover:underline text-[11px] font-semibold">Delete</button>}
        </div>
      </div>
      {renderCardExtras?.(p)}
    </div>
  );
}
